<script setup>
// Один коментар у гілці (серія, аніме, чат сайту, кліп).
//
// Той самий зміст, що й у рядка чату ефіру (ChatLiveMessage) — нік своїм
// кольором, значок, щит модератора, смайли, згадки, до пʼяти вкладень під
// спойлером, — тільки розкладка коментарна: аватарка, імʼя з датою над
// текстом і рядок дій під ним (оцінки, відповідь).
//
// Старі коментарі, перенесені з comments.app чи з найперших твічевих,
// приходять тим самим повідомленням з позначкою legacy: у них є аватарка з
// того сервісу, але немає акаунта на сайті — тож ні згадки, ні модерації
// автора, лише видалення.

const props = defineProps({
  message: { type: Object, required: true },
  meId: { type: Number, default: null },
  meName: { type: String, default: '' },
  canModerate: { type: Boolean, default: false },
  canReply: { type: Boolean, default: false },
  flash: { type: Boolean, default: false }
})

const emit = defineEmits(['delete', 'mute', 'reply', 'react', 'mention', 'jump'])

const media = useStrapiMedia

const isMine = computed(() => !!props.meId && props.message.userId === props.meId)
const color = computed(() => props.message.color || nickColor(props.message.author))
const badge = computed(() => (props.message.badge ? media(props.message.badge) : ''))

const hasMedia = computed(() => {
  const list = props.message.media
  return Array.isArray(list) ? list.length > 0 : !!list
})

const LEGACY = {
  telegram: 'Перенесено з коментарів Telegram (comments.app)',
  twitch: 'Перенесено зі старих коментарів сайту'
}

const legacyTitle = computed(() =>
  props.message.legacy ? LEGACY[props.message.legacy] || 'Старий коментар' : ''
)

const mentionsMe = computed(() => {
  const name = props.meName
  if (!name || !props.message.text || props.message.deleted) return false
  return props.message.text
    .split(/\s+/)
    .some((token) => token.toLowerCase().replace(/[^a-z0-9_.@-]+$/i, '') === `@${name.toLowerCase()}`)
})

const quote = computed(() => props.message.replyTo || null)
const quoteText = computed(() => {
  const parent = quote.value
  if (!parent) return ''
  if (parent.deleted) return 'коментар видалено'
  return parent.text || (parent.media ? 'медіа' : '')
})

const time = computed(() => {
  const d = new Date(props.message.at)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const clock = d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === now.toDateString()) return clock
  const date = d.toLocaleDateString('uk-UA', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' })
  })
  return `${date} ${clock}`
})

const fullTime = computed(() => {
  const d = new Date(props.message.at)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('uk-UA')
})

const MUTES = [
  { label: '1 год', seconds: 3600 },
  { label: '1 день', seconds: 86400 },
  { label: '7 днів', seconds: 7 * 86400 }
]
const menuOpen = ref(false)

const mute = (seconds) => {
  menuOpen.value = false
  emit('mute', { userId: props.message.userId, seconds })
}
</script>

<template>
  <div
    :id="`chat-msg-${message.id}`"
    class="group relative flex gap-2 px-1.5 py-1 rounded transition-colors"
    :class="[
      mentionsMe ? 'bg-warning/10 border-warning border-l-2' : isMine ? 'bg-primary/5' : '',
      flash ? 'ring-1 ring-primary/70' : ''
    ]"
  >
    <!-- Аватарка: фото зі старих коментарів або значок із налаштувань чату,
         на весь кружечок (object-cover). Нема ні того, ні іншого — літера -->
    <CommentAvatar
      :author="message.author"
      :avatar="message.avatar || badge"
      class="mt-0.5 shrink-0"
    />

    <div class="flex-1 min-w-0">
      <div class="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-xs">
        <span
          v-if="message.moderator"
          class="inline-flex text-primary"
          title="Модератор"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm-1 14-4-4 1.4-1.4L11 13.2l4.6-4.6L17 10l-6 6Z" />
          </svg>
        </span>

        <span
          class="max-w-[180px] font-semibold truncate"
          :class="canReply && !message.legacy ? 'cursor-pointer hover:underline' : ''"
          :style="{ color }"
          @click="canReply && !message.legacy && emit('mention', message.author)"
          >{{ message.author }}</span
        >

        <VerifiedBadge v-if="message.verified" />

        <span
          v-if="message.legacy"
          class="bg-base-content/10 px-1 rounded text-[9px] text-base-content/50 uppercase tracking-wide"
          :title="legacyTitle"
          >{{ message.legacy === 'twitch' ? 'архів' : 'tg' }}</span
        >

        <span
          class="text-[10px] text-base-content/40"
          :title="fullTime"
          >{{ time }}</span
        >
      </div>

      <button
        v-if="quote"
        type="button"
        class="flex items-center gap-1 my-0.5 pl-1.5 border-primary/40 border-l max-w-full text-[11px] text-left text-base-content/50 hover:text-base-content/80"
        @click="quote.id && emit('jump', quote.id)"
      >
        <span
          class="font-medium shrink-0"
          :style="{ color: quote.color || nickColor(quote.author) }"
          >{{ quote.author }}</span
        >
        <span
          class="truncate"
          :class="{ italic: quote.deleted }"
          >{{ quoteText }}</span
        >
      </button>

      <p
        v-if="message.deleted"
        class="text-[12px] text-base-content/40 italic"
      >
        коментар видалено{{ message.deletedBy ? ` · ${message.deletedBy}` : '' }}
      </p>

      <template v-else>
        <ChatMessageText
          v-if="message.text"
          class="text-sm break-words whitespace-pre-line"
          :text="message.text"
          :max="hasMedia ? 0 : 3"
          :me="meName"
        />
        <ChatMediaGrid
          v-if="hasMedia"
          :media="message.media"
          :spoiler="message.spoiler"
        />

        <div class="flex items-center gap-3 mt-0.5 text-[11px] text-base-content/45">
          <button
            type="button"
            class="flex items-center gap-0.5 transition-colors"
            :class="message.mine === 1 ? 'text-primary' : 'hover:text-primary'"
            title="Подобається"
            @click="emit('react', message, 1)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66a4.8 4.8 0 0 0-.82-1.1L14 2L7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.84A2.34 2.34 0 0 0 9.34 20h8.11c.7 0 1.36-.37 1.72-.97z"
              />
            </svg>
            <span v-if="message.likes">{{ message.likes }}</span>
          </button>

          <button
            type="button"
            class="flex items-center gap-0.5 transition-colors"
            :class="message.mine === -1 ? 'text-error' : 'hover:text-error'"
            title="Не подобається"
            @click="emit('react', message, -1)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              class="rotate-180"
            >
              <path
                fill="currentColor"
                d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66a4.8 4.8 0 0 0-.82-1.1L14 2L7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.84A2.34 2.34 0 0 0 9.34 20h8.11c.7 0 1.36-.37 1.72-.97z"
              />
            </svg>
            <span v-if="message.dislikes">{{ message.dislikes }}</span>
          </button>

          <button
            v-if="canReply"
            type="button"
            class="hover:text-primary transition-colors"
            @click="emit('reply', message)"
          >
            Відповісти
          </button>

          <button
            v-if="canModerate || isMine"
            type="button"
            class="opacity-0 group-hover:opacity-100 hover:text-error transition"
            @click="emit('delete', message.id)"
          >
            Видалити
          </button>

          <div
            v-if="canModerate && message.userId && !isMine"
            class="relative opacity-0 group-hover:opacity-100 transition"
            :class="{ 'opacity-100': menuOpen }"
          >
            <button
              type="button"
              class="hover:text-warning"
              @click="menuOpen = !menuOpen"
            >
              Мовчанка
            </button>
            <div
              v-if="menuOpen"
              class="bottom-5 left-0 z-40 absolute flex flex-col bg-base-200 shadow-xl border border-base-content/10 rounded overflow-hidden"
            >
              <button
                v-for="option in MUTES"
                :key="option.seconds"
                class="hover:bg-base-300 px-3 py-1 text-left whitespace-nowrap"
                @click="mute(option.seconds)"
              >
                {{ option.label }}
              </button>
              <button
                class="hover:bg-base-300 px-3 py-1 border-base-content/10 border-t text-left whitespace-nowrap"
                @click="mute(0)"
              >
                Зняти
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
